import { useState } from "react";
import { Dialog } from "./Dialog";

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="copy"
      aria-label={done ? `${label} copied` : `Copy ${label}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1400);
        });
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        {done ? (
          <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" />
        ) : (
          <g fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="5" width="8.5" height="8.5" rx="1.5" /><path d="M10.5 2.5h-7a1 1 0 0 0-1 1v7" /></g>
        )}
      </svg>
    </button>
  );
}

export function shortId(value: string, head = 10, tail = 6): string {
  return value.length > head + tail + 1 ? `${value.slice(0, head)}…${value.slice(-tail)}` : value;
}

/**
 * Long identifier (digest, address, run id) shown shortened with copy and a labelled full-view action,
 * so two values that share a prefix are never indistinguishable. showLabel prints the label before the value.
 */
export function CopyableId({ value, label, head = 10, tail = 6, href, showLabel, full }: {
  value: string;
  label: string;
  head?: number;
  tail?: number;
  href?: string;
  showLabel?: boolean;
  /** render the complete value inline (wrapping) instead of shortening */
  full?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const shown = full ? value : shortId(value, head, tail);
  const truncated = shown !== value;
  const code = <code title={value} translate="no">{shown}</code>;
  return (
    <span className="copyable-id">
      {showLabel ? <span className="id-label">{label}</span> : null}
      {href ? <a href={href} target="_blank" rel="noreferrer" aria-label={`${label} ${value} (opens explorer)`}>{code}</a> : code}
      <CopyButton value={value} label={label} />
      {truncated ? (
        <button type="button" className="copy" aria-label={`View full ${label}`} onClick={() => setOpen(true)}>
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 8h12M10 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
        </button>
      ) : null}
      {open ? (
        <Dialog title={`Full ${label}`} onClose={() => setOpen(false)} actions={<CopyButton value={value} label={label} />}>
          <p className="id-full">{value}</p>
        </Dialog>
      ) : null}
    </span>
  );
}
